/**
 * Icon Primitive
 * Semantic icon component with size variants and curated icon set
 *
 * PATTERN: This file demonstrates the design system's dynamic token generation pattern:
 * 1. Define values once in a source object (SIZE_CONFIG, ICON_MAP)
 * 2. Derive TypeScript types from the source (IconSizeToken, IconTypeToken)
 * 3. Generate constants dynamically for autocomplete (IconSize, IconType)
 *
 * Benefits:
 * - Single source of truth (no duplication)
 * - Type-safe (TypeScript enforces valid values)
 * - Autocomplete support (Icon.size.md, Icon.type.email)
 * - Easy to extend (just add to SIZE_CONFIG or ICON_MAP)
 *
 * Usage:
 * <Icon type="email" size="md" />
 * <Icon type={Icon.type.task} size={Icon.size.lg} />
 */

import React from "react";
import * as Icons from "design-system/icons";
import { TextColorToken } from "../foundation";
import { Color } from "../foundation/Color";
import { useColorModeValue } from "../hooks/useColorModeValue";

// Size configuration - single source of truth for sizes and their properties
const SIZE_CONFIG = {
  xs: { boxSize: "12px" },
  sm: { boxSize: "16px" },
  base: { boxSize: "20px" },
  md: { boxSize: "20px" },
  lg: { boxSize: "24px" },
  xl: { boxSize: "32px" },
} as const;

// Type is derived from SIZE_CONFIG
export type IconSizeToken = keyof typeof SIZE_CONFIG;

// Icon mapping - curated set of commonly used icons
const ICON_MAP = {
  plus: Icons.Plus,
  clock: Icons.Clock,
  email: Icons.Email,
  filter: Icons.Filter,
  task: Icons.Task,
  meeting: Icons.Meeting,
  message: Icons.Message,
  oneOnOne: Icons.OneOnOne,
  groupSession: Icons.GroupSession,
  recurring: Icons.Recurring,
  search: Icons.Search,
  sort: Icons.Sort,
  x: Icons.X,
} as const;

// Type is derived from ICON_MAP
export type IconTypeToken = keyof typeof ICON_MAP;

export interface IconProps {
  type?: IconTypeToken;
  size?: IconSizeToken;
  color?: TextColorToken;
  customIcon?: React.ReactNode; // Escape hatch for custom icons
  "data-testid"?: string;
}

function IconComponent({
  type,
  size = "base",
  color,
  customIcon,
  "data-testid": testId,
}: IconProps) {
  // Resolve color mode at the top to follow hooks rules
  const resolvedColor = useColorModeValue(color || Color.text.primary);

  // If custom icon is provided, use that instead
  if (customIcon) {
    return customIcon;
  }

  // Must have either type or customIcon
  if (!type) {
    console.warn("Icon component requires either 'type' or 'customIcon' prop");
    return null;
  }

  const IconComponentFromMap = ICON_MAP[type];

  if (!IconComponentFromMap) {
    console.warn(`Icon "${type}" not found in icon map`);
    return null;
  }

  const sizeProps = SIZE_CONFIG[size];

  return (
    <IconComponentFromMap
      {...sizeProps}
      color={resolvedColor}
      data-testid={testId}
    />
  );
}

// Dynamically generate size constants from SIZE_CONFIG for discoverability and autocomplete
export const IconSize = Object.keys(SIZE_CONFIG).reduce(
  (acc, size) => {
    acc[size as IconSizeToken] = size as IconSizeToken;
    return acc;
  },
  {} as Record<IconSizeToken, IconSizeToken>,
);

// Dynamically generate icon type constants from ICON_MAP keys for discoverability
export const IconType = Object.keys(ICON_MAP).reduce(
  (acc, key) => {
    acc[key as IconTypeToken] = key as IconTypeToken;
    return acc;
  },
  {} as Record<IconTypeToken, IconTypeToken>,
);

// Escape hatch for custom icons
const customIconHelper = (customIcon: React.ReactNode) => ({
  customIcon,
  type: undefined,
  size: "base" as IconSizeToken,
});

// Public API - combines component with helpers and constants
export const Icon = Object.assign(IconComponent, {
  custom: customIconHelper,
  size: IconSize,
  type: IconType,
});
